import styled from '@emotion/styled';
import { EventTypes } from '@racemap/utilities/consts/events';
import TestSelectors from '@racemap/utilities/consts/test_selectors';
import { isActivated } from '@racemap/utilities/functions/event';
import {
  eventIsNeitherFreeNorPaid,
  isAtomicEvent,
  isChildEvent,
  isGroupEvent,
} from '@racemap/utilities/functions/utils';
import { Button, Dropdown, type MenuProps, Space } from 'antd';
import type { Immutable } from 'immer';
import { DateTime } from 'luxon';
import type { FC } from 'react';
import { useNavigate } from 'react-router';
import { copyIntoClipBoard } from 'src/components/utils/copyIntoClipBoard';
import { useIsPhone, useIsTinyPhone } from 'src/lib/customHooks';
import { upTo } from 'src/lib/customHooks/useBreakpoint';
import { useCurrentUser } from 'src/lib/customHooks/useCurrentUser';
import { useIsSafemap } from 'src/lib/customHooks/useIsSafemap';
import { getLeaderboardUrl } from 'src/lib/getLeaderboardUrl';
import { getPlayerUrl } from 'src/lib/getPlayerUrl';
import { getSafemapUrl } from '../../../lib/EventUtils';
import type { CurrentEvent } from '../../../store/events/events_reducers';
import {
  IconCopy,
  IconEllipsis,
  IconLeaderboard,
  IconMap,
  IconSafemap,
} from '../../BasicComponents/Icons';
import { PopoverHint } from '../../BasicComponents/PopoverHint';
import { PayButton } from './PayButton';

interface Props {
  event: Immutable<CurrentEvent>;
}

interface LinkButtonProps extends Props {
  /** Drops the label, keeping it as a tooltip. */
  iconOnly?: boolean;
}

export const Buttons: FC<Props> = ({ event }) => {
  const user = useCurrentUser();
  const isSafemap = useIsSafemap();
  const isPhone = useIsPhone();

  if (user == null) return null;

  return (
    <Container>
      {isAtomicEvent(event) && eventIsNeitherFreeNorPaid(event) && (
        <PayButton event={event} user={user} />
      )}
      <Space.Compact>
        {isSafemap ? (
          <SafemapButton event={event} iconOnly={isPhone} />
        ) : (
          <PlayerButton event={event} iconOnly={isPhone} />
        )}
        <OverflowMenu event={event} />
      </Space.Compact>
    </Container>
  );
};

export const PlayerButton: FC<LinkButtonProps> = ({ event, iconOnly = false }) => {
  const label = eventIsNeitherFreeNorPaid(event) ? 'Preview' : 'Tracking Map';

  return (
    <Button
      href={getPlayerUrl(event)}
      target="_blank"
      rel="noopener noreferrer"
      icon={<IconMap />}
      title={label}
      className={TestSelectors.admin.eventEditor.titleBar.previewButton.toString()}
    >
      {!iconOnly && (
        <Space size={4}>
          {label}
          {isGroupEvent(event) && (
            <PopoverHint placement="bottom">
              On desktop applications, the group URL preselects specific single events,
              automatically. During the event, ALL RUNNING single events in the group are selected,
              automatically. After the event, the 1st single event in group settings is selected,
              automatically.
            </PopoverHint>
          )}
        </Space>
      )}
    </Button>
  );
};

const SafemapButton: FC<LinkButtonProps> = ({ event, iconOnly = false }) => {
  const label = eventIsNeitherFreeNorPaid(event) ? 'Preview' : 'Safemap';

  return (
    <Button
      href={getSafemapUrl(event)}
      target="_blank"
      rel="noopener noreferrer"
      icon={<IconSafemap />}
      title={label}
      className={TestSelectors.admin.eventEditor.titleBar.previewButton.toString()}
    >
      {!iconOnly && label}
    </Button>
  );
};

const OverflowMenu: FC<Props> = ({ event }) => {
  const navigate = useNavigate();
  const isTinyPhone = useIsTinyPhone();
  const previewUrl = useIsSafemap() ? getSafemapUrl(event) : getPlayerUrl(event);
  const leaderboardUrl = getLeaderboardUrl(event);
  const finishedTwoWeeksAgo = DateTime.fromISO(event.endTime) < DateTime.now().minus({ weeks: 2 });

  const items: MenuProps['items'] = (
    [
      isActivated(event.modules.timing)
        ? {
            key: 'leaderboard',
            icon: <IconLeaderboard />,
            label: (
              <MenuRow>
                <a href={leaderboardUrl} target="_blank" rel="noopener noreferrer">
                  Leaderboard
                </a>
                <Button
                  type="text"
                  size="small"
                  icon={<IconCopy />}
                  title="Copy leaderboard link"
                  onClick={(e) => {
                    e.stopPropagation();
                    copyIntoClipBoard(leaderboardUrl);
                  }}
                />
              </MenuRow>
            ),
          }
        : null,
      finishedTwoWeeksAgo
        ? {
            key: 'duplicate',
            icon: <IconCopy />,
            label: 'Duplicate Tracking Map',
            onClick: () => navigate(`/admin/events/${event.id}/advanced/duplicate`),
          }
        : null,
      isChildEvent(event)
        ? {
            key: 'group',
            icon: <IconMap />,
            label: `Go to ${event.type === EventTypes.STAGE ? 'stage' : 'contest'} group`,
            onClick: () => navigate(`/admin/events/${event.parent}`),
          }
        : null,
      { type: 'divider' as const },
      {
        key: 'copy-preview-link',
        icon: <IconCopy />,
        label: 'Copy preview link',
        onClick: () => copyIntoClipBoard(previewUrl),
      },
    ] satisfies MenuProps['items']
  ).filter((item) => item != null);

  return (
    <Dropdown
      menu={{ items }}
      trigger={['click']}
      placement="bottomRight"
      styles={isTinyPhone ? { root: { maxHeight: 200, overflow: 'auto' } } : undefined}
    >
      <Button icon={<IconEllipsis />} title="More links" aria-label="More links" />
    </Dropdown>
  );
};

const Container = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: none;

  @media ${upTo('phone')} {
    gap: 5px;
  }
`;

const MenuRow = styled.span`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;
